import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiSave, FiX } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import toast from 'react-hot-toast';
import { api } from '../utils/api';

interface Assistant {
  id: string;
  name: string;
  description: string;
  model: string;
  status: 'active' | 'inactive' | 'training';
  channels: string[];
  instructions?: string;
  temperature?: number;
  maxTokens?: number;
  color?: string;
}

const AssistantEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assistant, setAssistant] = useState<Assistant>({
    id: '',
    name: '',
    description: '',
    model: 'gpt-4-turbo-preview',
    status: 'active',
    channels: [],
    instructions: '',
    temperature: 0.7,
    maxTokens: 2048,
    color: 'from-blue-500 to-cyan-500'
  });

  const availableChannels = [
    { id: 'webchat', label: 'Web Chat', icon: '🌐' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
    { id: 'sms', label: 'SMS', icon: '📱' },
    { id: 'email', label: 'Email', icon: '✉️' },
    { id: 'slack', label: 'Slack', icon: '💼' },
    { id: 'phone', label: 'Phone', icon: '📞' }
  ];

  const availableModels = [
    { id: 'gpt-4-turbo-preview', label: 'GPT-4 Turbo' },
    { id: 'gpt-4', label: 'GPT-4' },
    { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { id: 'claude-3-opus', label: 'Claude 3 Opus' },
    { id: 'claude-3-sonnet', label: 'Claude 3 Sonnet' }
  ];

  useEffect(() => {
    if (id) {
      fetchAssistant();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchAssistant = async () => {
    try {
      const response = await api.get(`/assistants/${id}`);
      setAssistant(response.data.assistant);
    } catch (error) {
      console.error('Failed to fetch assistant:', error);
      toast.error('Failed to load assistant');
      navigate('/assistants');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!assistant.name || !assistant.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (assistant.channels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    setSaving(true);
    try {
      if (id) {
        await api.put(`/assistants/${id}`, assistant);
        toast.success('Assistant updated successfully');
      } else {
        const response = await api.post('/assistants', assistant);
        toast.success('Assistant created successfully');
        navigate(`/assistants/${response.data.assistant.id}`);
        return;
      }
      navigate(`/assistants/${id}`);
    } catch (error) {
      toast.error(id ? 'Failed to update assistant' : 'Failed to create assistant');
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channelId: string) => {
    setAssistant(prev => ({
      ...prev,
      channels: prev.channels.includes(channelId)
        ? prev.channels.filter(c => c !== channelId)
        : [...prev.channels, channelId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(id ? `/assistants/${id}` : '/assistants')}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              >
                <FiArrowLeft size={20} />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <BiBot size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white">
                  {id ? 'Edit Assistant' : 'Create Assistant'}
                </h1>
              </div>
            </div>
            
            <div className="flex gap-2">
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(id ? `/assistants/${id}` : '/assistants')}
                className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all flex items-center gap-2"
              >
                <FiX size={16} />
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                <FiSave size={16} />
                {saving ? 'Saving...' : 'Save'}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Form Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Basic Information */}
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-bold text-white mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="assistant-name" className="block text-sm font-medium text-gray-300 mb-2">
                  Assistant Name *
                </label>
                <input
                  type="text"
                  id="assistant-name"
                  value={assistant.name}
                  onChange={(e) => setAssistant({ ...assistant, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder="e.g., Customer Support Bot"
                  required
                />
              </div>

              <div>
                <label htmlFor="assistant-description" className="block text-sm font-medium text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  id="assistant-description"
                  value={assistant.description}
                  onChange={(e) => setAssistant({ ...assistant, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder="Describe what this assistant does..."
                  rows={3}
                  required
                />
              </div>

              <div>
                <label htmlFor="assistant-status" className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  aria-label="status"
                  id="assistant-status"
                  value={assistant.status}
                  onChange={(e) => setAssistant({ ...assistant, status: e.target.value as any })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="active">Active</option>
                  <option value="training">Training</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Model Configuration */}
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-bold text-white mb-4">Model Configuration</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="assistant-model" className="block text-sm font-medium text-gray-300 mb-2">
                  AI Model
                </label>
                <select
                  aria-label="model"
                  id="assistant-model"
                  value={assistant.model}
                  onChange={(e) => setAssistant({ ...assistant, model: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                  {availableModels.map(model => (
                    <option key={model.id} value={model.id}>{model.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Temperature ({assistant.temperature})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={assistant.temperature}
                  onChange={(e) => setAssistant({ ...assistant, temperature: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="assistant-max-tokens" className="block text-sm font-medium text-gray-300 mb-2">
                  Max Tokens
                </label>
                <input
                  type="text"
                  id="assistant-max-tokens"
                  value={String(assistant.maxTokens ?? '')}
                  onChange={(e) => setAssistant({ ...assistant, maxTokens: parseInt(e.target.value || '0') })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  min="256"
                  max="4096"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Instructions
              </label>
              <textarea
                value={assistant.instructions}
                onChange={(e) => setAssistant({ ...assistant, instructions: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                placeholder="Provide specific instructions for how the assistant should behave..."
                rows={4}
              />
            </div>
          </div>

          {/* Channels */}
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-bold text-white mb-4">Channels *</h2>
            <p className="text-gray-400 mb-4">Select where this assistant will be available</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {availableChannels.map(channel => (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() => toggleChannel(channel.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    assistant.channels.includes(channel.id)
                      ? 'bg-cyan-600/20 border-cyan-500 text-cyan-400'
                      : 'bg-gray-700/50 border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{channel.icon}</span>
                  <span className="text-sm font-medium">{channel.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </form>
    </div>
  );
};

export default AssistantEdit;